import React, { useState } from "react";
import { DocumentReadResponse } from "models/api/response.types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import CollectionSelector from "components/helpers/CollectionSelector";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentOrganizationId } from "store/features/session/slice";
import { useDocumentCollections } from "api/documentCollectionService";
import { addAlert } from "store/features/general/slice";
import { useTranslation } from "react-i18next";

const AddToCollectionDialog: React.FC<{
  documents: DocumentReadResponse[];
  setOpen: (open: boolean) => void;
}> = ({ documents, setOpen }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [collectionId, setCollectionId] = useState(-1);
  const currentOrganizationId = useSelector(selectCurrentOrganizationId);
  const { getCachedCollectionById, updateCollectionMutation } =
    useDocumentCollections(currentOrganizationId);

  const addDocumentsToCollection = () => {
    const [collection] = getCachedCollectionById(collectionId);
    if (collection) {
      updateCollectionMutation.mutate(
        {
          collections: [
            {
              id: collection.id,
              document_ids: [
                ...collection.document_ids,
                ...documents.map((doc) => doc.id),
              ],
            },
          ],
        },
        {
          onSuccess: () => {
            dispatch(
              addAlert({
                severity: "success",
                autoHideDuration: 5000,
                alert: {
                  message: t("documents_added_to_collection", {
                    collectionName: collection.name,
                  }),
                },
              })
            );
            setOpen(false);
          },
          onError: () => {
            setOpen(false);
          },
        }
      );
    }
  };

  return (
    <Dialog
      open
      onClose={() => {
        setOpen(false);
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        {t("add_documents_to_collection")}
        <IconButton onClick={() => setOpen(false)}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <CollectionSelector
          includeDefaultCollections
          canCreateCollection
          currentParentId={collectionId}
          collectionToUse={(id?: number) => {
            setCollectionId(id || -1);
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          disabled={collectionId === -1}
          size="medium"
          color="primary"
          onClick={() => {
            addDocumentsToCollection();
          }}
        >
          {t("add")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddToCollectionDialog;
